type Params = {
  onSuccess?: () => void
  onError?: (error?: { response: { status: number; data: { message: string } } }) => void
}

export function useOauthServices({ onSuccess, onError }: Params = {}) {
  const { $analytics } = useNuxtApp()
  const hasError = ref(false)
  const config = useRuntimeConfig()
  const { createWithAlternateService, onLoginSuccess } = useUser()

  /**
   * Initiate the oauth authentication and sharing protocol window and when
   * finished get the user object and log in with the token.
   * @param {String} serviceEndpoint - the API endpoint at which the logic containing the handling
   * for the service is (e.g. for LinkedIn, it would be /auth/linkedin/web)
   */
  function connectWithAlternateService(serviceEndpoint: string, loginOrSignup: 'login' | 'signup') {
    const oauthWindow = window.open(
      `${config.public.API_ENDPOINT}${serviceEndpoint}?next=/v2/auth/?close=${window.location}`,
      'oauthWindow',
    )

    window.onmessage = async ({ data }) => {
      const isInternalAdminSsoError = typeof data === 'object' && data !== null && 'message' in data
      const isToken = typeof data === 'string'

      if (isInternalAdminSsoError) {
        const message = (data as { message: string }).message
        const error = {
          response: {
            status: 403,
            data: {
              message,
            },
          },
        }

        if (onError) {
          onError(error)
        } else {
          oauthError()
        }
        window.onmessage = null
      }

      if (isToken) {
        // Some OAuth providers/pages are opened in a context where COOP blocks window.close().
        try {
          oauthWindow?.close()
        } catch {
          // no-op
        }

        try {
          await createWithAlternateService()

          if (onSuccess) {
            onSuccess()
          } else {
            oauthSuccess()
          }
        } catch (error: any) {
          const errorEvent = loginOrSignup === 'login' ? 'loginError' : 'signUpSubmittedError'

          $analytics.track(errorEvent, {
            errorMessage: error?.response?.data?.message ? error.response.data.message : error,
          })

          if (onError) {
            onError(error)
          } else {
            oauthError()
          }
        } finally {
          window.onmessage = null
        }
      }
    }
  }

  function oauthSuccess() {
    onLoginSuccess()
  }

  function oauthError() {
    hasError.value = true

    setTimeout(() => {
      hasError.value = false
    }, 10000)
  }

  return {
    hasError,
    oauthError,
    oauthSuccess,
    connectWithAlternateService,
  }
}
